
import {first} from 'rxjs/operators';
import { Component, OnInit, Input, EventEmitter, Output } from "@angular/core";
import { BehaviorSubject } from "rxjs";

import { BaseControlComponent } from "../../../../../../form-controls/components/base-control/base-control.component";
import { QueryService } from "../../../../../../common/services/query.service";
import { DataParameter, DatabaseType, DataQuery, queryResultsHaveChanged } from "../../../../../../common/models/query.model";
import { deepClone } from "../../../../../models/editor.model";
import { ContextInteractionService } from '../../../../../../form-controls/services/context-interaction.service';


@Component({
    selector: "data-query-button-data-config",
    templateUrl: "./data-query-button-data-config.component.html",
    styleUrls: ["./data-query-button-data-config.component.scss"],
    standalone: false
})
export class DataQueryButtonDataConfigComponent extends BaseControlComponent implements OnInit {
    @Input()
    componentData: any;
    @Output()
    onQuerySelected: EventEmitter<DataQuery> = new EventEmitter();
    @Output()
    onParamChanged: EventEmitter<void> = new EventEmitter();

    static componentName = "DataQueryButtonDataConfigComponent";

    readonly parameterNote: string = "To use a constant for a parameter, simply enter the value in the field.  To use the context value of another question, be sure to target the .value node of the context.  (E.g., to use the value of a question whose context is $.data.answer, use $.data.answer.value.) ";
    readonly resultNote: string = "Results will be stored in the configured context location, under a sub-node named \"results\", as the named properties stored above.  (E.g., a \"Name\" property will be stored under $.data.contextLocation.results.name.)";
    readonly resultNote2: string = "If there are multiple results, they will be stored in an array in the referenced location.  (E.g., multiple results would be stored in $.data.contextLocation.results as [{ \"name\": resultValue }].)";
    readonly resultNote3: string = "If results are displayed as a table, an html version of the table will also be generated and stored in the configured context location, under a sub-node named \"htmlResults\"; Unless a separate location is specified below."

    loadingQueries$ = new BehaviorSubject<boolean>(false);
    
    defaultQuery = {
        id: "-1",
        name: "...",
        collection: "",
        database: DatabaseType.Document,
        parameters: [],
        resultValues: [],
        queryText: ""
    } as DataQuery;
    parameterSource: string = "input";
    queries: DataQuery[] = [];
    queryResultsChanged: boolean = false;
    selectedQueryId: string;
    htmlResultsContextTarget: string;

    

    constructor(public readonly queryService: QueryService, public readonly contextService: ContextInteractionService) {
        super(contextService);
    }

    ngOnInit() {
        if (!this.componentData.config.query) {
            this.componentData.config.query = this.defaultQuery;
            this.selectedQueryId = this.defaultQuery.id;
        } else {
            this.selectedQueryId = this.componentData.config.query.id;
        }
        for (let p = 0; p < this.componentData.config.query.parameters.length; p++) {
            if (!this.componentData.config.query.parameters[p].source) {
                this.componentData.config.query.parameters[p].source = "input";
            }
        }
        this.loadQueries();
    }

    loadQueries(): void {
        this.loadingQueries$.next(true);
        this.queryService.getAll().pipe(first()).subscribe(data => {
            this.loadingQueries$.next(false);
            this.queries.push(this.defaultQuery);
            for (let q = 0; q < data.length; q++) {
                this.queries.push(data[q] as DataQuery);
                if (this.componentData.config.query.id === data[q].id) {
                    this.queryResultsChanged = queryResultsHaveChanged(this.componentData.config.query, data[q])
                    if (this.queryResultsChanged) {
                        this.componentData.config.query.resultValues = data[q].resultValues;
                    }
                }
            } 
        },
        (error) => {
            this.queries.push(this.defaultQuery);
            console.error(error);
            this.loadingQueries$.next(false);
        });
    }

    selectedQueryChanged(): void {
        this.queryResultsChanged = false;
        const selected = this.queries.find(q => q.id === this.selectedQueryId);
        if (selected) {
            this.componentData.config.query = deepClone(selected);
            this.componentData.config.query.queryText = ""; //Don't save a copy of the query text with the config; rely on loading the query at run-time
        } else {
            this.componentData.config.query = deepClone(this.defaultQuery);
        }

        this.onQuerySelected.emit(this.componentData.config.query);
    }

    sourceChanged(param: DataParameter): void {
        if (param.source !== "input") {
            param.value = "";
        }

        this.onParamChanged.emit(null);
    }
}
